import { captureException } from '@sentry/browser'
import { useEffect, useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import type { PaymentDialogProps } from 'src/components/Payment/PaymentDialog'
import { PAYMENT_PROVIDERS } from 'src/components/Payment/PaymentMethodSelectItem'
import { orderPaymentProviderIdsForCountry } from 'src/components/Payment/paymentProviderOrdering'
import { useMyMembership } from 'src/services/apollo/membership'
import { useIPCountry } from 'src/services/helpers/ip'
import { PaymentProvider } from 'src/services/helpers/payment'
import dayjs from 'src/utils/dayjs'

function useSelectedPaymentProvider(
  eligibleProviders: {
    provider: PaymentProvider
    preferred: boolean
  }[],
  purchaseType: 'membership' | 'credit-pack' | 'unknown',
  initialPaymentProvider?: PaymentProvider,
) {
  const [selectedPaymentProvider, setSelectedPaymentProvider] =
    useState<PaymentProvider | null>(null)
  const initialPaymentProviderAppliedRef = useRef(false)

  const { data: { subscription } = {}, loading: isLoadingMembership } =
    useMyMembership()

  const hasValidProvider = eligibleProviders.length >= 1
  const firstEligibleProvider = hasValidProvider
    ? eligibleProviders[0].provider
    : null
  const eligibleProviderKey = eligibleProviders
    .map(({ provider }) => provider)
    .join('|')
  const initialEligiblePaymentProvider = eligibleProviders.find(
    ({ provider }) => provider === initialPaymentProvider,
  )?.provider

  useEffect(() => {
    if (!firstEligibleProvider) {
      return
    }

    if (
      initialEligiblePaymentProvider &&
      !initialPaymentProviderAppliedRef.current &&
      eligibleProviderKey
    ) {
      initialPaymentProviderAppliedRef.current = true
      setSelectedPaymentProvider(initialEligiblePaymentProvider)
      return
    }

    if (initialPaymentProviderAppliedRef.current) {
      return
    }

    if (!isLoadingMembership && subscription && purchaseType === 'membership') {
      const lastSubscriptionEndTime = dayjs(subscription.endAt)

      // last subscription is not expired, and last use PayPay, use the same payment provider
      if (lastSubscriptionEndTime.isAfter(dayjs())) {
        const provider = subscription.provider
        if (provider === 'paypay') {
          setSelectedPaymentProvider(PaymentProvider.paypay)
          return
        }
      }
    }

    setSelectedPaymentProvider(firstEligibleProvider)
  }, [
    firstEligibleProvider,
    subscription,
    isLoadingMembership,
    purchaseType,
    initialEligiblePaymentProvider,
    eligibleProviderKey,
  ])

  return {
    singularProvider: eligibleProviders.length === 1,
    selectedPaymentProvider,
    setSelectedPaymentProvider,
  }
}

export function usePaymentProvider(props: PaymentDialogProps) {
  const {
    data: ipCountry,
    isLoading: ipCountryLoading,
    error: ipCountryError,
  } = useIPCountry()
  const hasReportedIpCountryErrorRef = useRef(false)

  useEffect(() => {
    if (ipCountryError && !hasReportedIpCountryErrorRef.current) {
      hasReportedIpCountryErrorRef.current = true
      captureException(ipCountryError, {
        tags: { feature: 'payment' },
        extra: { hook: 'useIPCountry' },
      })
    }
  }, [ipCountryError])

  // Surface subscription state to provider predicates. Apollo dedupes
  // with useSelectedPaymentProvider below — no extra request.
  const { data: { subscription } = {} } = useMyMembership()
  const hasActiveSubscription =
    !!subscription && dayjs(subscription.endAt).isAfter(dayjs())

  const purchaseType = match(props.itemId)
    .returnType<'membership' | 'credit-pack' | 'unknown'>()
    .when(
      item => item?.includes('membership'),
      () => 'membership' as const,
    )
    .when(
      item => item?.includes('extra-package'),
      () => 'credit-pack' as const,
    )
    .otherwise(() => 'unknown' as const)

  const eligibleProviders = useMemo(() => {
    // IP country resolution states:
    //
    // | Scenario       | isLoading | data      | error | ipCountryResolved | ipCountryUnavailable | Result                    |
    // |----------------|-----------|-----------|-------|-------------------|----------------------|---------------------------|
    // | Loading        | true      | -         | -     | false             | -                    | return [] (show spinner)  |
    // | Success        | false     | 'JP'      | null  | true              | false                | normal country filtering  |
    // | Fetch failed   | false     | undefined | Error | true              | true                 | show all providers        |
    // | Empty response | false     | undefined | null  | true              | true                 | show all providers        |
    const ipCountryResolved = !ipCountryLoading || ipCountryError != null
    if (!ipCountryResolved) return []

    // Country unavailable: covers both fetch error and successful response with empty data
    const ipCountryUnavailable = !ipCountry

    const availablePaymentProviders = PAYMENT_PROVIDERS.filter(p => {
      if (typeof p.available === 'function') {
        // Cannot determine country: show all providers as fallback
        if (ipCountryUnavailable) return true
        return p.available({
          country: ipCountry,
          type: purchaseType,
          prices: props.prices,
          hasActiveSubscription,
        })
      } else {
        return p.available
      }
    })

    const preferredPaymentProvider =
      props.paymentProvider?.preferred?.provider ??
      availablePaymentProviders.find(
        p =>
          p.preferred?.({
            type: purchaseType,
            prices: props.prices,
            hasActiveSubscription,
          }) ?? false,
      )?.id

    const allowed = props.paymentProvider.allowed
    const allAvailableIds = orderPaymentProviderIdsForCountry(
      availablePaymentProviders.map(p => p.id),
      ipCountry ?? undefined,
    )
    const providers = allowed === 'any' ? allAvailableIds : allowed
    const processed = providers
      .filter(el => allAvailableIds.includes(el))
      .map(p => ({
        provider: p,
        preferred: p === preferredPaymentProvider,
      }))
    // sort by preferred, then by order of appearance in `PAYMENT_PROVIDERS`
    processed.sort((a, b) => {
      if (a.preferred && !b.preferred) return -1
      if (!a.preferred && b.preferred) return 1
      return (
        allAvailableIds.indexOf(a.provider) -
        allAvailableIds.indexOf(b.provider)
      )
    })
    return processed
  }, [
    props.paymentProvider.allowed,
    props.paymentProvider.preferred?.provider,
    ipCountry,
    ipCountryLoading,
    ipCountryError,
    hasActiveSubscription,
    purchaseType,
    props.prices,
  ])

  const { selectedPaymentProvider, setSelectedPaymentProvider } =
    useSelectedPaymentProvider(
      eligibleProviders,
      purchaseType,
      props.initialPaymentProvider,
    )

  return {
    loading: ipCountryLoading,
    eligibleProviders,
    selectedPaymentProvider,
    setSelectedPaymentProvider,
  }
}
